import React from 'react';
import PropTypes from 'prop-types';
import ProgressIndicator from './ProgressIndicator';

const TasksTable = ({
	tasks,
	parentTasks,
	totalHours,
	selectedUserNames,
	multiMode,
	separateView,
	showDetails,
	startDate,
	endDate,
	isDataStillUpdating,
	loading,
	tasksProcessed,
	totalTasksToProcess,
	employeeMetrics = {},
}) => {
	// Helper function to get task link with fallback to parent task
	const getTaskLink = task => {
		if (task.projects && task.projects.length > 0) {
			return `https://app.asana.com/0/${task.projects[0].gid}/${task.originalTaskGid}`;
		}

		if (task.isSubtask && task.parentGid && parentTasks[task.parentGid]) {
			const parentTask = parentTasks[task.parentGid];
			if (parentTask.projects && parentTask.projects.length > 0) {
				return `https://app.asana.com/0/${parentTask.projects[0].gid}/${task.originalTaskGid}`;
			}
		}

		return `https://app.asana.com/0/0/${task.gid}`;
	};

	// Get task display name with parent info for subtasks
	const getTaskDisplayName = task => {
		if (!task.isSubtask) return task.name;

		const parentTask = task.parentGid ? parentTasks[task.parentGid] : null;
		if (parentTask) {
			return `${task.name} -> ${parentTask.name}`;
		}

		return `${task.name} [Subtask]`;
	};

	// Format the user names for the header
	const formatUserNames = () => {
		if (!selectedUserNames || selectedUserNames.length === 0) return "No users";
		if (selectedUserNames.length === 1) return selectedUserNames[0];
		if (selectedUserNames.length === 2) return `${selectedUserNames[0]} and ${selectedUserNames[1]}`;
		return `${selectedUserNames.slice(0, -1).join(', ')} and ${selectedUserNames[selectedUserNames.length - 1]}`;
	};

	// Function to get color class based on hours vs estimate comparison
	const getHoursColorClass = (actualHours, estimatedHours) => {
		if (!estimatedHours || estimatedHours <= 0) {
			return 'text-gray-900';
		}

		const percentageOver = ((actualHours - estimatedHours) / estimatedHours) * 100;

		if (percentageOver >= 50) return 'text-red-600 font-bold';
		if (percentageOver >= 30) return 'text-yellow-600 font-bold';
		return 'text-gray-900';
	};

	// Helper function to get color class based on QA/Design points
	const getPointsColorClass = (points) => {
		if (!points || points === 0 || points >= 80) return '';
		if (points >= 70) return 'text-blue-600 font-semibold';
		if (points >= 60) return 'text-yellow-600 font-semibold';
		if (points >= 50) return 'text-orange-600 font-semibold';
		return 'text-red-600 font-semibold';
	};

	// Check if a user belongs to designers department
	const isUserDesigner = (userName) => {
		const data = employeeMetrics[userName];
		return data && data.department === 'designers';
	};

	// Group tasks by user for separate view mode
	const getTasksByUser = () => {
		const userTasks = {};
		const userHours = {};

		selectedUserNames.forEach(name => {
			userTasks[name] = [];
			userHours[name] = 0;
		});

		tasks.forEach(task => {
			if (task.userName && userTasks.hasOwnProperty(task.userName)) {
				userTasks[task.userName].push(task);
				userHours[task.userName] += task.actualHours;
			}
		});

		return Object.keys(userTasks)
			.filter(name => userTasks[name].length > 0)
			.map(name => ({
				userName: name,
				tasks: userTasks[name],
				hours: userHours[name],
				isDesigner: isUserDesigner(name),
			}));
	};

	// Render a single task table (used in both combined and separate views)
	const renderTaskTable = (tasksToRender, userTotalHours, showUserColumn = true, userName = null) => {
		const totalEstimatedHours = tasksToRender.reduce((sum, task) => sum + (task.estimatedHours || 0), 0);
		const totalStoryPoints = tasksToRender.reduce((sum, task) => sum + (task.storyPoints || 0), 0);
		const totalQAPoints = tasksToRender.reduce((sum, task) => sum + (task.qaPoints || 0), 0);
		const totalDesignPoints = tasksToRender.reduce((sum, task) => sum + (task.designPoints || 0), 0);

		// For combined view with mixed users, determine columns per-task
		// For pure designer tables: show DP, hide QA
		// For pure non-designer tables: show QA, hide DP
		// For mixed: show both (each user sees their relevant column)
		const hasDesignerTasks = tasksToRender.some(t => isUserDesigner(t.userName));
		const hasNonDesignerTasks = tasksToRender.some(t => !isUserDesigner(t.userName));
		const showQACol = hasNonDesignerTasks;
		const showDPCol = hasDesignerTasks;

		return (
		<>
		<table className='min-w-full text-sm border-collapse'>
			<thead className='bg-gray-50'>
				<tr>
					<th className='p-2 text-left'>Task ({tasksToRender.length})</th>
					<th className='p-2 text-left'>Project</th>
					{showUserColumn && multiMode && <th className='p-2 text-left'>User</th>}
					{showDetails && (
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-est-tasks"
							data-tooltip-content="Estimated hours for this task"
						>
							Est.
						</th>
					)}
					{showDetails && (
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-sp-tasks"
							data-tooltip-content="Story Points assigned to this task"
						>
							SP
						</th>
					)}
					{showDetails && showQACol && (
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-qa-tasks"
							data-tooltip-content="QA Points for this task"
						>
							QA
						</th>
					)}
					{showDetails && showDPCol && (
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-dp-tasks"
							data-tooltip-content="Design Points for this task"
						>
							DP
						</th>
					)}
					<th
						className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
						data-tooltip-id="tooltip-hours-tasks"
						data-tooltip-content="Actual hours tracked for this task in the selected period"
					>
						Hours
					</th>
				</tr>
			</thead>
			<tbody>
				{tasksToRender.map(t => {
					const taskUserIsDesigner = isUserDesigner(t.userName);
					return (
					<tr key={t.gid} className='border-t hover:bg-gray-50'>
						<td className='p-2 max-w-xs truncate'>
							<a
								href={getTaskLink(t)}
								target='_blank'
								rel='noopener noreferrer'
								className='text-blue-600 hover:underline'
							>
								{getTaskDisplayName(t)}
							</a>
						</td>
						<td className='p-2 truncate max-w-xs'>
							{t.projectNames ||
								(t.isSubtask &&
								parentTasks[t.parentGid]?.projects?.length > 0 ? (
									<span>
										{parentTasks[t.parentGid].projects
											.map(p => p.name)
											.join(', ')}
									</span>
								) : t.isSubtask ? (
									<span className='text-gray-500 italic'>
										No project info
									</span>
								) : (
									''
								))}
						</td>
						{showUserColumn && multiMode && (
							<td className='p-2'>
								{t.userName || ''}
							</td>
						)}
						{showDetails && (
							<td className='p-2 text-right'>
								{t.estimatedHours !== undefined && t.estimatedHours !== null ? t.estimatedHours.toFixed(2) : '0.00'}
							</td>
						)}
						{showDetails && (
							<td className='p-2 text-right'>
								{t.storyPoints ? t.storyPoints.toFixed(1) : '0'}
							</td>
						)}
						{showDetails && showQACol && (
							<td className={`p-2 text-right ${!taskUserIsDesigner ? getPointsColorClass(t.qaPoints) : 'text-gray-300'}`}>
								{!taskUserIsDesigner ? (t.qaPoints ? t.qaPoints.toFixed(1) : '0') : '—'}
							</td>
						)}
						{showDetails && showDPCol && (
							<td className={`p-2 text-right ${taskUserIsDesigner ? getPointsColorClass(t.designPoints) : 'text-gray-300'}`}>
								{taskUserIsDesigner ? (t.designPoints ? t.designPoints.toFixed(1) : '0') : '—'}
							</td>
						)}
						<td className={`p-2 text-right font-bold ${getHoursColorClass(t.actualHours, t.estimatedHours)}`}>
							{t.actualHours.toFixed(2)}
						</td>
					</tr>
					);
				})}
				<tr className='border-t font-bold bg-gray-50'>
					<td colSpan={
						(showUserColumn && multiMode ? 1 : 0) + 2
					} className='p-2'>
						Total{userName ? ` for ${userName}` : ''}
					</td>
					{showDetails && (
						<td className='p-2 text-right'>
							{totalEstimatedHours !== undefined && totalEstimatedHours !== null ? totalEstimatedHours.toFixed(2) : ''}
						</td>
					)}
					{showDetails && (
						<td className='p-2 text-right'>
							{totalStoryPoints > 0 ? totalStoryPoints.toFixed(1) : ''}
						</td>
					)}
					{showDetails && showQACol && (
						<td className='p-2 text-right'>
							{totalQAPoints > 0 ? totalQAPoints.toFixed(1) : ''}
						</td>
					)}
					{showDetails && showDPCol && (
						<td className='p-2 text-right'>
							{totalDesignPoints > 0 ? totalDesignPoints.toFixed(1) : ''}
						</td>
					)}
					<td className='p-2 text-right font-bold'>{userTotalHours.toFixed(2)}</td>
				</tr>
			</tbody>
		</table>

		</>
		);
	};

	return (
		<div className='mt-6 overflow-x-auto'>
			<h3 className='text-lg font-bold mb-2'>
				Tasks for {formatUserNames()} •{startDate.toISOString().slice(0, 10)}–
				{endDate.toISOString().slice(0, 10)}
			</h3>

			<div className='mb-4 font-semibold flex items-center'>
				<span>Total time: {totalHours.toFixed(2)} h</span>
				{isDataStillUpdating && (
					<span className='ml-2 text-xs text-blue-500 animate-pulse'>
						(updating...)
					</span>
				)}
			</div>

			{(loading || isDataStillUpdating) && (
				<ProgressIndicator
					tasksProcessed={tasksProcessed}
					totalTasksToProcess={totalTasksToProcess}
				/>
			)}

			{!separateView || selectedUserNames.length <= 1 ? (
				// Combined view (original)
				renderTaskTable(tasks, totalHours, true)
			) : (
				// Separate view (grid layout) - each user gets their own table
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
					{getTasksByUser().map(userGroup => (
						<div key={userGroup.userName} className='border rounded-lg p-2 bg-white overflow-auto'>
							<h4 className='font-medium mb-2'>{userGroup.userName}</h4>
							{renderTaskTable(userGroup.tasks, userGroup.hours, false, userGroup.userName)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

TasksTable.propTypes = {
	tasks: PropTypes.arrayOf(
		PropTypes.shape({
			gid: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
			projectNames: PropTypes.string,
			actualHours: PropTypes.number.isRequired,
			parentGid: PropTypes.string,
			isSubtask: PropTypes.bool,
			userName: PropTypes.string,
			storyPoints: PropTypes.number,
			qaPoints: PropTypes.number,
			designPoints: PropTypes.number,
			projects: PropTypes.arrayOf(
				PropTypes.shape({
					gid: PropTypes.string.isRequired,
					name: PropTypes.string.isRequired,
				})
			),
		})
	).isRequired,
	parentTasks: PropTypes.object.isRequired,
	totalHours: PropTypes.number.isRequired,
	selectedUserNames: PropTypes.arrayOf(PropTypes.string).isRequired,
	multiMode: PropTypes.bool,
	separateView: PropTypes.bool,
	showDetails: PropTypes.bool,
	startDate: PropTypes.instanceOf(Date).isRequired,
	endDate: PropTypes.instanceOf(Date).isRequired,
	isDataStillUpdating: PropTypes.bool.isRequired,
	loading: PropTypes.bool.isRequired,
	tasksProcessed: PropTypes.number.isRequired,
	totalTasksToProcess: PropTypes.number.isRequired,
	userRole: PropTypes.string,
	employeeMetrics: PropTypes.object,
};

export default TasksTable;
